import React, { useEffect, useMemo, useState } from 'react'
import { CompetencyEvaluation, CompetencyWithEvaluation, UpdateCompetencyEvaluationPayload } from '../../types/GrowthWheel'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSave } from '@fortawesome/free-solid-svg-icons'
import { useUpdateCompetencyEvaluationMutation } from '../../redux/growth-wheel.endpoints'
import LevelDefinitionsComponent from './LevelDefinitionsComponent'
import SelfAssessmentComponent from './SelfAssessmentComponent'
import GuidedAssessmentComponent from './GuidedAssessmentComponent'

interface CompetencyAssessmentContentProps {
  competency: CompetencyWithEvaluation
  canEditSelf?: boolean
  canEditGuided?: boolean
  userRole?: 'admin' | 'user'
}

interface AssessmentScoreState {
  selfLevel: number | null
  leaderLevel: number | null
  selfNotes: string
  guidedNotes: string
}

const getScoreFromEvaluation = (
  evaluation?: CompetencyEvaluation | null
): AssessmentScoreState => ({
  selfLevel: evaluation?.selfLevel ?? null,
  leaderLevel: evaluation?.leaderLevel ?? null,
  selfNotes: evaluation?.selfNote ?? '',
  guidedNotes: evaluation?.leaderNote ?? '',
})

const normalizeLevelDescriptions = (
  input: CompetencyWithEvaluation['levelDescriptions'] | string[] | undefined
) => {
  if (!input) {
    return {}
  }

  if (Array.isArray(input)) {
    return input.reduce<Record<number, string>>((acc, description, index) => {
      const level = index + 1
      acc[level] = description
      return acc
    }, {})
  }

  return input
}

const CompetencyAssessmentContent = ({
  competency,
  canEditSelf = true,
  canEditGuided = false,
  userRole = 'user',
}: CompetencyAssessmentContentProps) => {
  const normalizedLevelDescriptions = useMemo(
    () => normalizeLevelDescriptions(competency.levelDescriptions),
    [competency.levelDescriptions]
  )
  const levels = useMemo(() => {
    const keys = Object.keys(normalizedLevelDescriptions)
      .map(Number)
      .filter((value) => !Number.isNaN(value))
      .sort((a, b) => a - b)
    return keys.length ? keys : [1, 2, 3, 4, 5]
  }, [normalizedLevelDescriptions])
  
  const [currentScore, setCurrentScore] = useState<AssessmentScoreState>(() =>
    getScoreFromEvaluation(competency.evaluation)
  )
  const [initialScore, setInitialScore] = useState<AssessmentScoreState>(() =>
    getScoreFromEvaluation(competency.evaluation)
  )

  const [saveMessage, setSaveMessage] = useState('')
  const [updateCompetencyEvaluation, { isLoading: isSaving }] =
    useUpdateCompetencyEvaluationMutation()

  const defaultLevelDescriptions = useMemo(() => {
    if (Object.keys(normalizedLevelDescriptions).length) {
      return normalizedLevelDescriptions
    }
    return {}
  }, [normalizedLevelDescriptions])

  const [levelDescriptions, setLevelDescriptions] = useState<Record<number, string>>(
    defaultLevelDescriptions
  )

  useEffect(() => {
    const evaluationScore = getScoreFromEvaluation(competency.evaluation)
    setCurrentScore(evaluationScore)
    setInitialScore(evaluationScore)
    setSaveMessage('')
    setLevelDescriptions(defaultLevelDescriptions)
  }, [competency.id, competency.evaluation, defaultLevelDescriptions])

  const hasChanges = useMemo(
    () => JSON.stringify(currentScore) !== JSON.stringify(initialScore),
    [initialScore, currentScore]
  )

  const handleScoreChange = (
    field: keyof AssessmentScoreState,
    value: number | string
  ) => {
    setCurrentScore((prev) => ({
      ...prev,
      [field]: value,
    }))
    setSaveMessage('')
  }

  const handleCancel = () => {
    setCurrentScore(initialScore)
    setSaveMessage('')
  }

  const evaluationPayload = () => {
    const payload = canEditSelf ? {
      selfLevel: currentScore.selfLevel,
      selfNote: currentScore.selfNotes || null,
    } : canEditGuided ? {
      leaderLevel: currentScore.leaderLevel,
      leaderNote: currentScore.guidedNotes || null,
    } : null;


    return payload as UpdateCompetencyEvaluationPayload
    
  }

  const handleSave = async () => {
    const evaluationId = competency.evaluation?.id
    if (evaluationId == null) {
      setSaveMessage('Cannot save: evaluation not found')
      return
    }
    try {
      if(canEditSelf && currentScore.selfLevel === null) {
        setSaveMessage('Cannot save: self level is not selected')
        return
      }

      if(canEditGuided && currentScore.leaderLevel === null) {
        setSaveMessage('Cannot save: guided level is not selected')
        return
      }

      await updateCompetencyEvaluation({ evaluationId, payload: evaluationPayload() }).unwrap()
      setInitialScore(currentScore)
      setSaveMessage('Saved successfully')

    } catch {
      setSaveMessage('Failed to save. Please try again.')
    }
  }

  return (
    <div>
      <div className="row g-4">
        <div className="col-lg-6">
          <div className="card shadow-sm gw-assessment-card gw-assessment-card--self">
            <SelfAssessmentComponent
              levels={levels}
              canEditSelf={canEditSelf}
              handleScoreChange={handleScoreChange}
              currentScore={currentScore}
              levelDescriptions={levelDescriptions}
            />  
          </div>
        </div>

        <div className="col-lg-6">
          <div className="card shadow-sm gw-assessment-card gw-assessment-card--guided">
            <GuidedAssessmentComponent
              levels={levels}
              canEditGuided={canEditGuided}
              handleScoreChange={handleScoreChange}
              currentScore={currentScore}
              levelDescriptions={levelDescriptions}
            />
          </div>
        </div>
      </div>

      {userRole === 'admin' && (
        <LevelDefinitionsComponent
          userRole={userRole}
          defaultLevelDescriptions={defaultLevelDescriptions}
        />
      )}

      <div className="card shadow-sm mt-4 gw-assessment-actions">
        <div className="card-body d-flex align-items-center justify-content-between">
          <div className="d-flex flex-column gap-1">
            {saveMessage && (
              <span className="small text-success d-flex align-items-center gap-2">
                <FontAwesomeIcon icon={faSave} />
                {saveMessage}
              </span>
            )}
          </div>
          <div className="d-flex gap-2">
            {hasChanges && (
              <button
                onClick={handleCancel}
                className="gw-btn-cancel"
              >
                Cancel
              </button>
            )}
            <button
              onClick={() => handleSave()}
              disabled={!hasChanges || isSaving}
              className={`gw-btn-submit ${
                !hasChanges || isSaving ? 'disabled' : ''
              }`}
            >
              <FontAwesomeIcon icon={faSave} className="me-1" />
              {isSaving ? 'Saving...' : 'Save & Submit'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CompetencyAssessmentContent